import { useTransportQuery, type TransportResponse } from "@/transport"

import type { ConversationMetricRule } from "@/types/api/ConversationMetricRule"
import type { OrganizationRecord } from "@/types/api/OrganizationRecord"
import type { JsonObject } from "@/types/json"
import type { WorkflowGraph } from "@/types/WorkflowData"

type TransportOrganizationRecord =
  TransportResponse<"getOrganizationRecord">["organization"]
type TransportConversationMetricRule = NonNullable<
  NonNullable<TransportOrganizationRecord>["conditional_monitoring_metrics"]
>[number]
type TransportConversationMetricRuleWithMetadataConditions =
  TransportConversationMetricRule & {
    metadataConditions?: ConversationMetricRule["metadataConditions"]
  }

const ORG_RECORD_STALE_TIME_MS = 1000 * 60 * 5
const ORG_RECORD_RETRY_COUNT = 3
const ORG_RECORD_RETRY_DELAY_MS = 1000

interface UseGetOrgRecordOptions {
  enabled?: boolean
  refetchOnMount?: boolean
  refetchOnWindowFocus?: boolean
  retry?: number
  retryDelay?: number
  staleTime?: number
}

function normalizeConversationMetricRules(
  rules: NonNullable<
    NonNullable<TransportOrganizationRecord>["conditional_monitoring_metrics"]
  > = [],
): ConversationMetricRule[] {
  return rules.map((rule) => {
    const ruleWithMetadataConditions =
      rule as TransportConversationMetricRuleWithMetadataConditions

    return {
      id: rule.id,
      type: rule.type,
      metric: rule.metric ?? undefined,
      metrics: rule.metrics ?? undefined,
      metadataKey: rule.metadataKey,
      metadataType: rule.metadataType,
      matchType: rule.matchType,
      metadataValue: rule.metadataValue,
      metadataConditions:
        ruleWithMetadataConditions.metadataConditions ?? undefined,
    }
  })
}

function normalizeOrganizationRecord(
  organization: TransportOrganizationRecord,
): OrganizationRecord | null {
  if (!organization) {
    return null
  }

  return {
    id: organization.id,
    slug: organization.slug,
    display_name: organization.display_name,
    created_at: organization.created_at,
    last_updated_at: organization.last_updated_at,
    clerk_id: organization.clerk_id ?? null,
    partner_id: organization.partner_id ?? null,
    partner_customer_id: organization.partner_customer_id ?? null,
    metadata: organization.metadata ?? {},
    enabled_metrics: organization.enabled_metrics ?? [],
    default_monitoring_metrics: organization.default_monitoring_metrics ?? [],
    workflow_steps: (organization.workflow_steps ??
      {}) as unknown as WorkflowGraph,
    alert_thresholds: (organization.alert_thresholds ?? {}) as JsonObject,
    stripe_customer_id: organization.stripe_customer_id ?? null,
    langfuse_integration: organization.langfuse_integration ?? {},
    arize_integration: organization.arize_integration ?? {},
    zendesk_integration: organization.zendesk_integration ?? {},
    shelf_integration: organization.shelf_integration ?? {},
    coval_integration: organization.coval_integration ?? {},
    resolved_organization_wide_concurrency:
      organization.resolved_organization_wide_concurrency ?? null,
    resolved_max_job_concurrency:
      organization.resolved_max_job_concurrency ?? null,
    conditional_monitoring_metrics: normalizeConversationMetricRules(
      organization.conditional_monitoring_metrics,
    ),
    dashboards: (organization.dashboards ??
      {}) as OrganizationRecord["dashboards"],
  }
}

export const useGetOrgRecordTransport = (
  skipCache?: boolean,
  options?: UseGetOrgRecordOptions,
) => {
  return useTransportQuery<"getOrganizationRecord", OrganizationRecord | null>({
    operation: "getOrganizationRecord",
    request: { skip_cache: skipCache },
    options: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: ORG_RECORD_STALE_TIME_MS,
      retry: ORG_RECORD_RETRY_COUNT,
      retryDelay: ORG_RECORD_RETRY_DELAY_MS,
      select: (data) => normalizeOrganizationRecord(data.organization ?? null),
      ...options,
    },
  })
}
